import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBluelagoon, useTaskWebSocket } from "@bluelagoon";
import { toast } from "sonner";
import { useWorkflow } from "./WorkflowContext";
import { useWorkflowExecution } from "./WorkflowExecutionContext";
import { useCodeGeneration } from "./CodeGenerationContext";
import { useAuth } from "./AuthContext";
import { SecretAccessModal } from "../components/SecretAccessModal";
import { trackWorkflowUsage } from "@utils";
import { track } from "@crm/analytics";
import { useHumanSupport } from "../hooks/useHumanSupport";
import {
    capRequestLogsForTransmission,
    capConsoleLogsForTransmission,
} from "../Chat/ChatTwoColumnPage/appPreviewPayloadUtils";

const WorkflowGenerationContext = createContext();

function getHistoryForChat(messages, tasks = null) {
    // Filter messages based on compact message logic
    const filteredMessages = filterMessagesByCompact(messages).filter(
        (message) => message.role !== "diff"
    );

    // Find the index of the last AI message to append tasks to it
    let lastAIMessageIndex = -1;
    if (tasks && tasks.length > 0) {
        for (let i = filteredMessages.length - 1; i >= 0; i--) {
            if (
                filteredMessages[i].role === "assistant" ||
                filteredMessages[i].role === "compact"
            ) {
                lastAIMessageIndex = i;
                break;
            }
        }
    }

    return filteredMessages.map((message, index) => {
        // Build the task list string only for the last AI message
        let tasksContent = "";
        if (index === lastAIMessageIndex && tasks && tasks.length > 0) {
            const taskList = tasks
                .map((task) => {
                    const isCompleted =
                        task.status === "completed" || task.status === "done";
                    const check = isCompleted ? "[x]" : "[ ]";
                    let statusInfo = "";
                    if (task.status === "in_progress")
                        statusInfo = " (IN PROGRESS)";
                    if (task.status === "cancelled")
                        statusInfo = " (CANCELLED)";
                    const content = task.content || task.title || "No content";
                    return `- ${check} [id: ${task.id}] ${content}${statusInfo}`;
                })
                .join("\n");
            tasksContent = `\n\n<current_tasks>\nHere is the current task list you are working on:\n${taskList}\n</current_tasks>`;
        }

        return {
            role:
                message.role === "compact" || message.role === "diff"
                    ? "assistant"
                    : message.role,
            content:
                message.role === "diff"
                    ? `Here is the changes I made to the code: ${message.content}`
                    : message.content +
                      (message.language && message.language !== "none"
                          ? "\nSelected language: " + message.language
                          : "") +
                      (message.integrationTags &&
                      message.integrationTags.length > 0
                          ? "\nSelected integrations: " +
                            message.integrationTags?.join(", ")
                          : "") +
                      tasksContent,
            files: message.files,
        };
    });
}

function filterMessagesByCompact(messages) {
    if (!messages || messages.length === 0) {
        return [];
    }

    // Find the last compact message index
    let lastCompactIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "compact" || messages[i].isCompact === true) {
            lastCompactIndex = i;
            break;
        }
    }

    // If no compact message found, return all messages
    if (lastCompactIndex === -1) {
        return messages;
    }

    // If compact message is at the end (last message), return all messages
    if (lastCompactIndex === messages.length - 1) {
        return messages;
    }

    // Return from the last compact message forward
    return messages.slice(lastCompactIndex);
}

function countMessagesSinceLastCompact(messages) {
    if (!messages || messages.length === 0) {
        return 0;
    }

    // Find the last compact message index
    let lastCompactIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "compact" || messages[i].isCompact === true) {
            lastCompactIndex = i;
            break;
        }
    }

    // If no compact message found, return total message count
    if (lastCompactIndex === -1) {
        return messages.length;
    }

    // Return count of messages after the last compact
    return messages.length - lastCompactIndex - 1;
}

function getSelectedIntegrations(messages) {
    return messages.map((message) => message.integrationTags).flat();
}

/**
 * Collects all files from message history
 * @param {Array} messages - Array of messages with files
 * @returns {Array} - Deduplicated array of file objects
 */
function collectFilesFromHistory(messages) {
    if (!messages || messages.length === 0) {
        return [];
    }

    const filesMap = new Map();
    messages.forEach((message) => {
        if (message.files && Array.isArray(message.files)) {
            message.files.forEach((file) => {
                if (file && file.id) {
                    filesMap.set(file.id, file);
                }
            });
        }
    });

    return Array.from(filesMap.values());
}

/**
 * Groups messages by time while preserving messages within 2 minutes of the newest message
 * @param {Array} messages - Array of messages with createdAt timestamps
 * @returns {Object} - Object with grouped messages and preserved recent messages
 */
function groupMessagesByTime(messages) {
    if (!messages || messages.length === 0) {
        return { groupedMessages: [], recentMessages: [] };
    }

    // First, filter out messages before the last compact message
    const filteredMessages = filterMessagesByCompact(messages);

    // Sort messages by creation time (newest first)
    const sortedMessages = [...filteredMessages].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    const newestMessageTime = new Date(sortedMessages[0].createdAt);
    const now = new Date();
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000); // 30 minutes in milliseconds

    // If the newest message is older than 30 minutes, group all messages together (no time filtering)
    // This means the conversation is stale and can be fully compacted
    const shouldGroupAll = newestMessageTime < thirtyMinutesAgo;

    const recentMessages = [];
    const olderMessages = [];

    if (shouldGroupAll) {
        // When newest message is old, group all messages for compaction
        olderMessages.push(...sortedMessages);
    } else {
        // Use 5 minutes from NOW (current time) to preserve active conversations
        // This ensures that if a user is actively chatting, all messages within
        // the last 5 minutes are preserved, even if the conversation spans longer
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

        sortedMessages.forEach((message) => {
            const messageTime = new Date(message.createdAt);
            if (messageTime >= fiveMinutesAgo) {
                recentMessages.push(message);
            } else {
                olderMessages.push(message);
            }
        });
    }

    // Sort recent messages back to chronological order (oldest first)
    recentMessages.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    return {
        groupedMessages: olderMessages,
        recentMessages: recentMessages,
    };
}

function logGenerationPayloadDebug(label, payload) {
    const logs = Array.isArray(payload?.logs) ? payload.logs : [];
    const history = Array.isArray(payload?.history) ? payload.history : [];
    const tasks = Array.isArray(payload?.tasks) ? payload.tasks : [];
    const requestSuccessLogs = Array.isArray(payload?.requestSuccessLogs)
        ? payload.requestSuccessLogs
        : [];
    const fileIds = Array.isArray(payload?.fileIds) ? payload.fileIds : [];
    const stepData = Array.isArray(payload?.stepData) ? payload.stepData : [];

    console.log(`[WorkflowGenerationContext] ${label} payload summary`, {
        workflowId: payload?.workflowId,
        stepId: payload?.stepId,
        mode: payload?.mode,
        forceModel: payload?.forceModel,
        environmentName: payload?.environmentName,
        userRequestPreview: payload?.userRequest?.slice?.(0, 200),
        messagePreview: payload?.message?.slice?.(0, 200),
        contextLength: payload?.context?.length ?? 0,
        historyCount: history.length,
        logsCount: logs.length,
        logsPreview: logs.slice(0, 3),
        requestSuccessLogsCount: requestSuccessLogs.length,
        tasksCount: tasks.length,
        fileIdsCount: fileIds.length,
        stepDataCount: stepData.length,
    });
}

// --- Step Chat APIs ---
export function useStepChatAPIs() {
    // Get active step chat for a workflow step
    const {
        call: getActiveStepChat,
        error: getActiveStepChatError,
        isLoading: getActiveStepChatLoading,
    } = useBluelagoon({
        method: "getStepChatMessages", // This should match the backend method name
    });

    // Insert a message into the active step chat
    const {
        call: insertStepChatMessage,
        error: insertStepChatMessageError,
        isLoading: insertStepChatMessageLoading,
    } = useBluelagoon({
        method: "insertStepChatMessage", // This should match the backend method name
    });

    // Archive a step chat
    const {
        call: archiveStepChat,
        error: archiveStepChatError,
        isLoading: archiveStepChatLoading,
    } = useBluelagoon({
        method: "archiveStepChat", // This should match the backend method name
    });

    // Get step chat history
    const {
        call: getStepChatHistory,
        error: getStepChatHistoryError,
        isLoading: getStepChatHistoryLoading,
    } = useBluelagoon({
        method: "getStepChatHistory",
    });

    // Set active step chat
    const {
        call: setActiveStepChat,
        error: setActiveStepChatError,
        isLoading: setActiveStepChatLoading,
    } = useBluelagoon({
        method: "setActiveStepChat",
    });

    // Rate step chat
    const {
        call: rateStepChat,
        error: rateStepChatError,
        isLoading: rateStepChatLoading,
    } = useBluelagoon({
        method: "rateStepChat",
    });

    return {
        getActiveStepChat,
        getActiveStepChatError,
        getActiveStepChatLoading,
        insertStepChatMessage,
        insertStepChatMessageError,
        insertStepChatMessageLoading,
        archiveStepChat,
        archiveStepChatError,
        archiveStepChatLoading,
        getStepChatHistory,
        getStepChatHistoryError,
        getStepChatHistoryLoading,
        setActiveStepChat,
        setActiveStepChatError,
        setActiveStepChatLoading,
        rateStepChat,
        rateStepChatError,
        rateStepChatLoading,
    };
}

// Set to true to enable test messages for performance testing
const ENABLE_TEST_MESSAGES = false;

// Generate test messages with complex markdown
function generateTestMessages(count = 500) {
    const messages = [];
    const roles = ["human", "assistant"];
    const markdownExamples = [
        `# Heading 1\n## Heading 2\n### Heading 3\n\nThis is a paragraph with **bold text**, *italic text*, and \`inline code\`.`,
        `Here's a code block:\n\`\`\`javascript\nfunction example() {\n  const data = { name: "test", value: 123 };\n  return data;\n}\n\`\`\``,
        `- List item 1\n- List item 2\n  - Nested item 2.1\n  - Nested item 2.2\n- List item 3`,
        `1. First ordered item\n2. Second ordered item\n3. Third ordered item\n\nWith some text in between.\n\n4. Fourth ordered item`,
        `> This is a blockquote\n> with multiple lines\n> of quoted text`,
        `Here's a table:\n\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Data 1   | Data 2   | Data 3   |\n| Data 4   | Data 5   | Data 6   |`,
        `Mixed content:\n\n**Bold statement** followed by a list:\n- Item with \`code\`\n- Item with [link](https://example.com)\n\nAnd a code block:\n\`\`\`python\ndef calculate(x, y):\n    return x + y\n\`\`\``,
        `Nested formatting: **bold with *italic* inside** and \`code with **bold**\``,
        `Long paragraph with lots of text to simulate real conversations. This includes technical details about API endpoints, database schemas, and implementation details. For example, consider a REST API that handles user authentication with JWT tokens, stores data in PostgreSQL, and uses Redis for caching. The implementation should handle edge cases like expired tokens, race conditions, and connection pooling.`,
        `## Step-by-step Instructions\n\n1. First, install dependencies:\n\`\`\`bash\nnpm install express mongodb\n\`\`\`\n\n2. Then create the server:\n\`\`\`javascript\nconst express = require('express');\nconst app = express();\napp.listen(3000);\n\`\`\`\n\n3. Finally, test it:\n- Start the server\n- Open browser\n- Navigate to localhost:3000`,
    ];

    const now = new Date();
    for (let i = 0; i < count; i++) {
        const role = roles[i % 2];
        const content = markdownExamples[i % markdownExamples.length];
        const timestamp = new Date(now.getTime() - (count - i) * 60000); // Each message 1 minute apart

        messages.push({
            role,
            content: `Message ${i + 1}: ${content}`,
            createdAt: timestamp.toISOString(),
            language: i % 5 === 0 ? "javascript" : "none",
            integrationTags: i % 10 === 0 ? ["ChatGPT"] : [],
            files: [
                {
                    name: `## Step-by-step Instructions\n\n1. First, install dependencies:\n\`\`\`bash\nnpm${i}.txt`,
                    content: `This is file ${i}`,
                },
            ],
        });
    }

    return messages;
}

function useProvideWorkflowGeneration({ workflowId }) {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const auth = useAuth();
    const handleGetCompanyAgentUICredit = auth?.handleGetCompanyAgentUICredit;
    const userLanguage = i18n.language;

    const {
        startCodeGeneration: startCodeGenerationContext,
        updateTaskStatus,
        completeCodeGeneration,
        isGeneratingCode: isGeneratingCodeFromContext,
        currentWorkflowId: currentWorkflowIdContext,
    } = useCodeGeneration();

    const [activeStep, setActiveStep] = useState(null);
    const [chatHelperMessages, setChatHelperMessages] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [hasMoreChatMessages, setHasMoreChatMessages] = useState(false);
    const [chatMessagesCursor, setChatMessagesCursor] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [userActionRequest, setUserActionRequest] = useState(null);
    const [chatMode, setChatMode] = useState("code_generation"); // "code_generation", "plan", "human_support"

    const [isGeneratingCode, setIsGeneratingCode] = useState(() => {
        if (workflowId === currentWorkflowIdContext) {
            return isGeneratingCodeFromContext;
        }
        return false;
    });
    const [isCompactingChatMessages, setIsCompactingChatMessages] =
        useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [processErrors, setProcessErrors] = useState([]);
    const currentMessage = React.useRef("");
    const didGenerateForStepId = React.useRef({});
    const [codeVersionNumber, setCodeVersionNumber] = useState(1);

    const [requestSuccessLogs, setRequestSuccessLogs] = useState([]);

    // Background task state
    const [backgroundTaskId, setBackgroundTaskId] = useState(null);
    const [backgroundTaskStatus, setBackgroundTaskStatus] = useState(null);
    const [isBackgroundTaskRunning, setIsBackgroundTaskRunning] =
        useState(false);
    const [partialFeedback, setPartialFeedback] = useState([]);
    const lastPartialFeedbackRef = React.useRef([]);
    const pollRetryCountRef = React.useRef(0);
    // Track whether we've checked for running tasks on this workflow
    const didCheckRunningTasksRef = React.useRef(null);
    // Track whether the last generation actually modified files
    const [lastGenerationModifiedFiles, setLastGenerationModifiedFiles] =
        useState(false);

    const [stepsConfig, setStepsConfig] = useState({});
    const [isChatLoading, setIsChatLoading] = useState(false);

    // Chat History State
    const [stepChatHistory, setStepChatHistory] = useState([]);
    const [chatHistoryCursor, setChatHistoryCursor] = useState(null);
    const [hasMoreChatHistory, setHasMoreChatHistory] = useState(false);
    const [isLoadingMoreChatHistory, setIsLoadingMoreChatHistory] = useState(false);
    const [selectedStepChatId, setSelectedStepChatId] = useState(null); // null means active chat
    const [chatRating, setChatRating] = useState(null); // 1=bad, 2=okay, 3=great

    // Human Support - using centralized hook
    // Note: hook is initialized after we have workflow info below

    // Current Page/View Context - tracks what page/panel user is viewing
    const [currentPageContext, setCurrentPageContext] = useState({
        selectedPageId: null,
        selectedPageName: null,
        selectedPageFilePath: null,
        selectedRightPanel: null, // "overview", "editor", "users", "domains", etc.
        appViewMode: "preview", // "preview", "dashboard", "chat"
        currentRoute: null, // The actual route in the iframe app (e.g., "/dashboard")
    });

    // Canvas step selection - tracks which step the user is viewing/clicking in the canvas NDV
    const [selectedCanvasStepId, setSelectedCanvasStepId] = useState(null);

    // Secret Access Modal State
    const [missingSecretWarnings, setMissingSecretWarnings] = useState([]);
    const [secretModalOpen, setSecretModalOpen] = useState(false);
    const [requestedSecrets, setRequestedSecrets] = useState([]);
    const [secretsToCreate, setSecretsToCreate] = useState([]);
    const [secretAccessResolve, setSecretAccessResolve] = useState(null);
    const [isGrantingAccess, setIsGrantingAccess] = useState(false);
    const [getActiveStepChatLoading, setGetActiveStepChatLoading] =
        useState(false);

    const bypassWarning = React.useRef(false);
    const {
        workflow,
        addStep,
        steps,
        deleteStep,
        setSteps,
        updateWorkflowStep,
        testData,
        refetchWorkflow,
    } = useWorkflow();

    // Human Support Hook - centralized ticket management
    const humanSupport = useHumanSupport({
        workflowId,
        workflowName: workflow?.name,
        enabled:
            !!workflowId && workflowId !== "null" && workflowId !== "undefined",
    });

    const {
        databasesResponse,
        stepExecutionResult,
        stepExecutionLogs,
        removeStepExecutionData,
        handleResetFailedStep,
        startWorkflow,
        stepIdRunning,
        setStepExecutionLogs,
    } = useWorkflowExecution();
    const didSetup = React.useRef(false);

    const { call: chatWithWorkflowCall, error: chatError } = useBluelagoon({
        method: "chatWithWorkflow",
    });
    const {
        call: generateStepContextCall,
        isLoading: isGeneratingStepContext,
        error: generateStepContextError,
    } = useBluelagoon({
        method: "generateStepContext",
    });

    const {
        call: generateCodeContextCall,
        isLoading: isGeneratingCodeContext,
        error: generateCodeContextError,
    } = useBluelagoon({
        method: "generateCodeContext",
    });

    const {
        call: rollbackStep,
        isLoading: isLoadingRollback,
        error: errorRollback,
    } = useBluelagoon({
        method: "rollbackWorkflowStep",
    });

    const {
        call: startCodeGeneration,
        isLoading: isStartingCodeGeneration,
        error: startCodeGenerationError,
    } = useBluelagoon({
        method: "startCodeGeneration",
    });

    const {
        call: getTaskStatus,
        isLoading: isGettingTaskStatus,
        error: getTaskStatusError,
    } = useBluelagoon({
        method: "getTaskStatus",
    });

    const {
        call: cancelCodeGeneration,
        isLoading: isCancellingCodeGeneration,
        error: cancelCodeGenerationError,
    } = useBluelagoon({
        method: "cancelCodeGeneration",
    });

    const { call: getRunningTasksForWorkflow } = useBluelagoon({
        method: "getRunningTasksForWorkflow",
    });

    const {
        call: compactChatConversation,
        error: compactChatConversationError,
        isLoading: compactChatConversationLoading,
    } = useBluelagoon({
        method: "compactChatConversation",
    });

    const {
        getActiveStepChat,
        insertStepChatMessage,

        insertStepChatMessageLoading,
        archiveStepChat,
        archiveStepChatError,
        archiveStepChatLoading,
        getStepChatHistory,
        getStepChatHistoryLoading,
        setActiveStepChat,
        rateStepChat,
    } = useStepChatAPIs();

    const { call: getSecrets } = useBluelagoon({
        method: "getSecrets",
    });

    const { call: associateSecretWithWorkflow } = useBluelagoon({
        method: "associateSecretWithWorkflow",
    });

    const { call: upsertSecret } = useBluelagoon({
        method: "upsertSecret",
    });

    const checkSecretAccess = useCallback(
        async (secretsToCheck) => {
            if (!secretsToCheck || secretsToCheck.length === 0) return true;

            try {
                // Fetch all secrets to check which ones exist and which ones the workflow has access to
                const response = await getSecrets({});
                const allSecrets = response?.data?.secrets || [];

                // Find secrets that match the requested names
                const matchingSecrets = allSecrets.filter((s) =>
                    secretsToCheck.includes(s.name)
                );

                // Filter for secrets that exist but the current workflow does not have access to
                const secretsNeedingAccess = matchingSecrets.filter(
                    (secret) => {
                        const hasAccess = secret.workflows?.some(
                            (w) => w.id === workflowId
                        );
                        return !hasAccess;
                    }
                );

                if (secretsNeedingAccess.length === 0) {
                    return true;
                }

                // If there are secrets needing access, prompt the user
                setRequestedSecrets(secretsNeedingAccess.map((s) => s.name));
                setSecretModalOpen(true);

                // Return a promise that resolves when the user handles the modal
                return new Promise((resolve) => {
                    setSecretAccessResolve(() => resolve);
                });
            } catch (error) {
                console.error("Error checking secret access:", error);
                // On error, we probably shouldn't block, or maybe we should?
                // Let's assume true to avoid blocking on API errors, or false to be safe.
                // Going with true for now to not break flow if API fails, but logging error.
                return true;
            }
        },
        [getSecrets, workflowId]
    );

    const handleGrantSecretAccess = useCallback(async () => {
        setIsGrantingAccess(true);
        try {
            const response = await getSecrets({});
            const allSecrets = response?.data?.secrets || [];
            const secretsToGrant = allSecrets.filter((s) =>
                requestedSecrets.includes(s.name)
            );

            for (const secret of secretsToGrant) {
                await associateSecretWithWorkflow({
                    secretId: secret.id,
                    workflowId,
                });
            }

            toast.success("Secret access granted");
            if (secretAccessResolve) secretAccessResolve(true);
        } catch (error) {
            console.error("Failed to grant secret access:", error);
            toast.error("Failed to grant access");
            if (secretAccessResolve) secretAccessResolve(false);
        } finally {
            setIsGrantingAccess(false);
            setSecretModalOpen(false);
            setRequestedSecrets([]);
            setSecretsToCreate([]);
            setSecretAccessResolve(null);
        }
    }, [
        associateSecretWithWorkflow,
        requestedSecrets,
        workflowId,
        secretAccessResolve,
        getSecrets,
    ]);

    const handleCreateSecrets = useCallback(
        async (items) => {
            setIsGrantingAccess(true);
            try {
                for (const item of items) {
                    const result = await upsertSecret({
                        name: item.name,
                        value: item.value,
                        description: item.description || undefined,
                    });
                    if (result?.data?.secret?.id) {
                        await associateSecretWithWorkflow({
                            secretId: result.data.secret.id,
                            workflowId,
                        });
                    }
                }
                // Also grant any existing secrets pending access
                if (requestedSecrets.length > 0) {
                    await handleGrantSecretAccess();
                    return;
                }
                toast.success("Secrets created and access granted");
                if (secretAccessResolve) secretAccessResolve(true);
            } catch (error) {
                console.error("Failed to create secrets:", error);
                toast.error(error?.message || "Failed to create secrets");
                if (secretAccessResolve) secretAccessResolve(false);
            } finally {
                setIsGrantingAccess(false);
                setSecretModalOpen(false);
                setRequestedSecrets([]);
                setSecretsToCreate([]);
                setSecretAccessResolve(null);
            }
        },
        [
            upsertSecret,
            associateSecretWithWorkflow,
            workflowId,
            secretAccessResolve,
            requestedSecrets,
            handleGrantSecretAccess,
        ]
    );

    const handleDenySecretAccess = useCallback(() => {
        if (secretAccessResolve) secretAccessResolve(false);
        setSecretModalOpen(false);
        setRequestedSecrets([]);
        setSecretsToCreate([]);
        setSecretAccessResolve(null);
    }, [secretAccessResolve]);

    const PAGE_SIZE = 50;

    const mapMessages = (messages) =>
        messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            functionCall: msg.functionCall,
            mode: msg.mode,
            functionResponse: msg.functionResponse,
            createdAt: msg.createdAt,
            language: msg.language,
            integrationTags: msg.integrationTags,
            toolCallId: msg.toolCallId,
            toolName: msg.toolName,
            thoughtSignature: msg.thoughtSignature,
            workflowStepVersionId: msg.workflowStepVersionId,
            creditsUsed: msg.creditsUsed,
            llmModel: msg.llmModel,
            files: msg.files?.map((fileRelation) => fileRelation.file) || [],
        }));

    // Fetch and set chat messages for the active step or specific chat
    const fetchStepChatMessages = useCallback(
        async (stepId, chatId = null) => {
            if (!workflowId) return;

            try {
                // Use test messages for performance testing
                if (ENABLE_TEST_MESSAGES) {
                    const testMessages = generateTestMessages(500);
                    setChatMessages(testMessages);
                    return;
                }

                if (chatId == null || chatId !== selectedStepChatId) {
                    setGetActiveStepChatLoading(true);
                }

                // Use workflowId for chat queries (decoupled from step)
                const params = { workflowId, limit: PAGE_SIZE };
                if (chatId != null) {
                    params.stepChatId = chatId;
                }
                const response = await getActiveStepChat(params);

                if (response?.status === 200 && response.stepChat) {
                    const mappedMessages = mapMessages(
                        response.stepChat.messages
                    );
                    setChatMessages(mappedMessages);
                    setChatRating(response.stepChat.rating || null);
                    setHasMoreChatMessages(
                        response.pagination?.hasMore ?? false
                    );
                    setChatMessagesCursor(
                        response.pagination?.nextCursor ?? null
                    );

                    setGetActiveStepChatLoading(false);

                    // Set the stepChatId from the loaded chat for subsequent API calls
                    // Only set if the chat is active to avoid race conditions with archiving
                    if (response.stepChat.id && response.stepChat.isActive) {
                        setSelectedStepChatId(response.stepChat.id);
                    }
                } else {
                    setChatMessages([]);
                    setChatRating(null);
                    setSelectedStepChatId(null);
                    setHasMoreChatMessages(false);
                    setChatMessagesCursor(null);
                    setGetActiveStepChatLoading(false);
                }
            } catch (error) {
                setChatMessages([]);
                setHasMoreChatMessages(false);
                setChatMessagesCursor(null);
                setGetActiveStepChatLoading(false);
            }
        },
        [getActiveStepChat, workflowId]
    );

    // Fetch older messages (prepend to existing)
    const fetchOlderChatMessages = useCallback(async () => {
        if (!workflowId || !hasMoreChatMessages || !chatMessagesCursor) return;

        try {
            const params = {
                workflowId,
                limit: PAGE_SIZE,
                cursor: chatMessagesCursor,
            };
            if (selectedStepChatId) {
                params.stepChatId = selectedStepChatId;
            }
            const response = await getActiveStepChat(params);

            if (response?.status === 200 && response.stepChat) {
                const olderMessages = mapMessages(response.stepChat.messages);
                setChatMessages((prev) => [...olderMessages, ...prev]);
                setHasMoreChatMessages(response.pagination?.hasMore ?? false);
                setChatMessagesCursor(response.pagination?.nextCursor ?? null);
            }
        } catch (error) {
            console.error("Error fetching older chat messages", error);
        }
    }, [
        getActiveStepChat,
        workflowId,
        hasMoreChatMessages,
        chatMessagesCursor,
        selectedStepChatId,
    ]);

    // Fetch chat history list (page 1). Resets cursor + hasMore.
    const fetchChatHistory = useCallback(async () => {
        if (!workflowId) return;
        try {
            const response = await getStepChatHistory({ workflowId });
            if (response?.status === 200 && response.stepChats) {
                setStepChatHistory(response.stepChats);
                setChatHistoryCursor(response.pagination?.nextCursor || null);
                setHasMoreChatHistory(Boolean(response.pagination?.hasMore));
            }
        } catch (error) {
            console.error("Error fetching chat history", error);
        }
    }, [getStepChatHistory, workflowId]);

    // Load the next page using the cursor. Appends to the existing list.
    const loadMoreChatHistory = useCallback(async () => {
        if (!workflowId || !chatHistoryCursor || isLoadingMoreChatHistory) return;
        setIsLoadingMoreChatHistory(true);
        try {
            const response = await getStepChatHistory({
                workflowId,
                cursor: chatHistoryCursor,
            });
            if (response?.status === 200 && response.stepChats) {
                setStepChatHistory((prev) => [...prev, ...response.stepChats]);
                setChatHistoryCursor(response.pagination?.nextCursor || null);
                setHasMoreChatHistory(Boolean(response.pagination?.hasMore));
            }
        } catch (error) {
            console.error("Error loading more chat history", error);
        } finally {
            setIsLoadingMoreChatHistory(false);
        }
    }, [getStepChatHistory, workflowId, chatHistoryCursor, isLoadingMoreChatHistory]);

    // Add a message to the active step chat
    const addStepChatMessage = useCallback(
        async ({
            stepId,
            role,
            content,
            mode,
            functionCall,
            functionResponse,
            language,
            integrationTags,
            fileIds,
            stepChatId,
        }) => {
            if (!workflowId) return;
            const response = await insertStepChatMessage({
                workflowId,
                role,
                content,
                mode,
                functionCall,
                functionResponse,
                language,
                integrationTags,
                fileIds,
                stepChatId,
            });
            // Refresh messages after insert
            await fetchStepChatMessages(stepId, response?.data?.stepChatId);

            return {
                stepChatId: response?.stepChatId,
            };
        },
        [insertStepChatMessage, fetchStepChatMessages]
    );

    // Chat is holistic — always use workflowId for StepChat persistence
    // This keeps the conversation in one place regardless of which step is active
    // Legacy chatStepId kept for backward compat with components that still reference it
    const chatStepId = React.useMemo(() => {
        return steps?.length > 0 ? steps[0].id : activeStep;
    }, [steps, activeStep]);

    // Archive a step chat
    const archiveStepChatForStep = useCallback(async () => {
        await archiveStepChat({
            workflowId,
        });

        setChatMessages([]);
        setTasks([]);
        setChatHelperMessages([]);
        setChatRating(null);
        setSelectedStepChatId(null); // Reset so next chat creates a new StepChat
        setHasMoreChatMessages(false);
        setChatMessagesCursor(null);
        setUserActionRequest(null);
    }, [archiveStepChat, workflowId]);

    // Switch to a specific chat session (history or active)
    const selectChatSession = useCallback(
        async (chatId) => {
            if (!workflowId) return;

            // If selecting a specific chat (not just clearing selection), make it active in backend
            if (chatId) {
                try {
                    // Try to set active on backend first
                    const response = await setActiveStepChat({
                        workflowId,
                        stepChatId: chatId,
                    });

                    if (response?.status !== 200) {
                        throw new Error("Failed to activate chat");
                    }

                    // If successful, update UI state
                    setSelectedStepChatId(chatId);
                    setChatMessages([]); // Clear while loading
                    setTasks([]);
                    setUserActionRequest(null);
                    // Refresh history to reflect active status change
                    fetchChatHistory();
                    await fetchStepChatMessages(null, chatId);
                } catch (error) {
                    console.error("Failed to set active chat session", error);
                    // Do not update selectedStepChatId or messages if failed
                }
            } else {
                // Determine what to do when clearing (going to "active" implicitly or new)
                // If chatId is null, we are just resetting to active view (which might be the current active or empty)
                // We assume there's always an active chat if we are in this view, or we just load whatever is active.
                setSelectedStepChatId(null);
                setChatMessages([]);
                setTasks([]);
                setUserActionRequest(null);
                await fetchStepChatMessages(null, null);
            }
        },
        [workflowId, fetchStepChatMessages, setActiveStepChat, fetchChatHistory]
    );

    // Start a new chat (archive active if exists, reset state)
    const startNewChat = useCallback(async () => {
        if (workflowId) {
            try {
                // Archive the current active chat (if any)
                await archiveStepChatForStep();
            } catch (err) {
                // Ignore archive errors - chat might already be archived
                console.log("Archive error (may be expected):", err);
            }

            // Refresh history list to show the archived chat
            fetchChatHistory();
        }
    }, [workflowId, archiveStepChatForStep, fetchChatHistory]);

    // Rate the current chat (1=bad, 2=okay, 3=great)
    const handleRateChat = useCallback(
        async (rating) => {
            if (!selectedStepChatId) return;
            try {
                const response = await rateStepChat({
                    stepChatId: selectedStepChatId,
                    rating,
                });
                if (response?.status === 200) {
                    setChatRating(rating);
                }
            } catch (error) {
                console.error("Error rating chat:", error);
            }
        },
        [selectedStepChatId, rateStepChat]
    );

    const activeStepData = React.useMemo(() => {
        return steps?.find((step) => step.id === activeStep);
    }, [activeStep, steps]);

    const removeStep = React.useCallback(
        async (stepId) => {
            try {
                const response = await deleteStep({ stepId });
                if (response?.status === 200) {
                    setSteps((prevSteps) =>
                        prevSteps.filter((step) => step.id !== stepId)
                    );
                    setStepsConfig((prev) =>
                        Object.fromEntries(
                            Object.entries(prev).filter(
                                ([key]) => key !== stepId
                            )
                        )
                    );

                    // Remove step execution data
                    removeStepExecutionData(stepId);

                    setActiveStep(null);
                    setChatMessages([]);
                    setChatHelperMessages([]);
                    setTasks([]);
                }
            } catch (error) {
                console.error("Error deleting step:", error);
            }
        },
        [deleteStep, removeStepExecutionData, setSteps, setActiveStep]
    );

    const handleSetStepsConfig = useCallback(
        (stepId, updatedConfig) => {
            setStepsConfig((prev) => {
                const newObj = {
                    ...prev,
                    [stepId]: {
                        ...prev[stepId],
                        ...updatedConfig,
                    },
                };

                updateWorkflowStep(stepId, {
                    config: newObj[stepId],
                    isDraft: true,
                });

                return newObj;
            });
        },
        [updateWorkflowStep]
    );

    const getStepConfig = useCallback(
        (stepId) => {
            return stepsConfig[stepId];
        },
        [stepsConfig]
    );

    const sendHelperMessage = useCallback(
        (message, action = "", buttonText = "") => {
            setChatHelperMessages((prev) => [
                { role: "assistant", content: message, action, buttonText },
            ]);
        },
        []
    );

    const appendHelperMessage = useCallback(
        (message, action = "", buttonText = "", data = null) => {
            setChatHelperMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: message,
                    action,
                    buttonText,
                    data,
                },
            ]);
        },
        []
    );

    const clearHelperListenersRef = useRef([]);
    const clearHelperMessages = useCallback(() => {
        setChatHelperMessages([]);
        for (const listener of clearHelperListenersRef.current) {
            listener();
        }
    }, []);
    const onClearHelperMessages = useCallback((listener) => {
        clearHelperListenersRef.current.push(listener);
        return () => {
            clearHelperListenersRef.current =
                clearHelperListenersRef.current.filter((l) => l !== listener);
        };
    }, []);

    const handleAddStep = React.useCallback(
        async (type = "CODE_EDITOR", config = {}) => {
            const step = await addStep(type, config);
            setActiveStep(step.id);
            // Chat is holistic — don't reload messages when adding a step
            // The chat stays on the first step's StepChat
        },
        [addStep, setActiveStep]
    );

    const handleGenerateCodeContext = useCallback(
        async ({ code, language, activeStep }) => {
            try {
                const response = await generateCodeContextCall({
                    code: code,
                    language: language,
                });

                updateWorkflowStep(activeStep, {
                    codeContext: response.data?.context,
                });

                return response.data;
            } catch (error) {
                console.log("error", error);
                return null;
            }
        },
        [generateCodeContextCall, handleSetStepsConfig]
    );

    const handleGenerateStepContext = useCallback(
        async ({ code, language, activeStep }) => {
            try {
                if (
                    didGenerateForStepId.current[activeStep] ||
                    (steps.find((step) => step.id === activeStep)?.name &&
                        steps.find((step) => step.id === activeStep)?.name !==
                            "CODE_EDITOR" &&
                        steps.find((step) => step.id === activeStep)
                            ?.description)
                ) {
                    return;
                }

                didGenerateForStepId.current[activeStep] = true;

                const response = await generateStepContextCall({
                    code: code,
                    language: language,
                    userLanguage: userLanguage,
                });

                updateWorkflowStep(activeStep, {
                    name: response.data?.name,
                    description: response.data?.description,
                    isDraft: true,
                });

                return response.data;
            } catch (error) {
                console.log("error", error);
                return null;
            }
        },
        [generateStepContextCall, handleSetStepsConfig, steps, userLanguage]
    );

    React.useEffect(() => {
        if (
            activeStep &&
            stepsConfig[activeStep]?.code &&
            activeStepData?.name === "CODE_EDITOR"
        ) {
            handleGenerateStepContext({
                code: stepsConfig[activeStep]?.code,
                language: stepsConfig[activeStep]?.language,
                activeStep: activeStep,
            });
        }
    }, [activeStep, stepsConfig]);

    const getCurrentStepData = useCallback(
        ({ activeStep, stepExecutionResult, steps }) => {
            const currentStepIndex = steps.findIndex(
                (step) => step.id === activeStep
            );

            const stepData =
                stepExecutionResult?.slice(
                    0,
                    currentStepIndex === -1
                        ? stepExecutionResult.length - 1
                        : currentStepIndex
                ) ?? [];

            return stepData;
        },
        []
    );

    const startBackgroundCodeGeneration = useCallback(
        async ({
            requestSuccessLogs,
            activeStep,
            environmentName,
            message,
            selectedApp,
            integrations,
            currentCode,
            history,
            language,
            processErrorsFinal,
            initialData,
            files,
            selectedIntegrations,
            mode = "code_generation",
            typeOfCodeGeneration = "new_code",
            forceModel = "auto",
            tasks = [],
            stepChatId,
            workflowType,
            context: passedContext = "", // Accept context from caller (e.g., page context from handleChat)
        }) => {
            try {
                setIsGeneratingCode(true);
                const currentStepIndex = steps.findIndex(
                    (step) => step.id === activeStep
                );

                const stepData = getCurrentStepData({
                    activeStep,
                    stepExecutionResult,
                    steps,
                });

                // Start with any context passed from caller (e.g., currentPageContext from handleChat)
                let context = passedContext || "";

                if (currentStepIndex > 0) {
                    const previousStep = steps.find(
                        (step, index) => index === currentStepIndex - 1
                    );

                    if (previousStep) {
                        // Add separator if we already have context from caller
                        const separator = context ? "\n\n" : "";
                        // Use context from the step itself (new approach)
                        if (previousStep.codeContext) {
                            context += `${separator}Here is what you did in the previous step: ${previousStep.codeContext}`;
                        }
                        // Fallback to config for backward compatibility
                        else if (stepsConfig[previousStep.id]?.context) {
                            context += `${separator}Here is what you did in the previous step: ${
                                stepsConfig[previousStep.id]?.context
                            }`;
                        }
                    }
                }

                // Include next step's code if it exists
                if (currentStepIndex < steps.length - 1) {
                    const nextStep = steps[currentStepIndex + 1];
                    if (nextStep && stepsConfig[nextStep.id]?.code) {
                        const nextStepCode = stepsConfig[nextStep.id].code;
                        const codeLines = nextStepCode.split("\n");

                        let limitedCode = nextStepCode;
                        if (codeLines.length > 300) {
                            // Find lines that contain stepData references
                            const stepDataLineIndices = [];
                            codeLines.forEach((line, index) => {
                                if (
                                    line.includes("stepData[") ||
                                    line.includes("stepData.at(")
                                ) {
                                    stepDataLineIndices.push(index);
                                }
                            });

                            if (stepDataLineIndices.length > 0) {
                                // Include stepData lines with context (5 lines before and after each)
                                const linesToInclude = new Set();
                                stepDataLineIndices.forEach((index) => {
                                    for (
                                        let i = Math.max(0, index - 5);
                                        i <=
                                        Math.min(
                                            codeLines.length - 1,
                                            index + 5
                                        );
                                        i++
                                    ) {
                                        linesToInclude.add(i);
                                    }
                                });

                                // If we still have room, add more lines from the beginning
                                let currentIndex = 0;
                                while (
                                    linesToInclude.size < 300 &&
                                    currentIndex < codeLines.length
                                ) {
                                    linesToInclude.add(currentIndex);
                                    currentIndex++;
                                }

                                // Sort and create the limited code
                                const sortedIndices = Array.from(
                                    linesToInclude
                                ).sort((a, b) => a - b);
                                const resultLines = [];
                                let lastIndex = -2;

                                sortedIndices.forEach((index) => {
                                    if (index > lastIndex + 1) {
                                        resultLines.push(
                                            "// ... [code omitted] ..."
                                        );
                                    }
                                    resultLines.push(codeLines[index]);
                                    lastIndex = index;
                                });

                                limitedCode = resultLines.join("\n");
                            } else {
                                // No stepData references, just take first 300 lines
                                limitedCode =
                                    codeLines.slice(0, 300).join("\n") +
                                    "\n// ... [remaining code omitted] ...";
                            }
                        }

                        context += `\n\nThe next step after this one contains the following code (for reference):\n${limitedCode}`;
                    }
                }

                // Check if any subsequent steps use this step's output
                // Skip the immediate next step (currentStepIndex + 1) since we already showed its full code above
                const stepsUsingCurrentOutput = [];
                for (let i = currentStepIndex + 2; i < steps.length; i++) {
                    const step = steps[i];
                    const stepCode = stepsConfig[step.id]?.code || "";
                    const codeLines = stepCode.split("\n");

                    // Check if the code references stepData with the current index
                    const usagePatterns = [
                        `stepData[${currentStepIndex}]`,
                        `stepData.at(${currentStepIndex})`,
                        `stepData.at(-${steps.length - currentStepIndex})`,
                    ];

                    const usageSnippets = [];
                    codeLines.forEach((line, lineIndex) => {
                        const trimmedLine = line.trim();
                        if (
                            usagePatterns.some((pattern) =>
                                line.includes(pattern)
                            )
                        ) {
                            // Include 2 lines before and 2 lines after for context
                            const startLine = Math.max(0, lineIndex - 2);
                            const endLine = Math.min(
                                codeLines.length - 1,
                                lineIndex + 2
                            );
                            const snippet = codeLines
                                .slice(startLine, endLine + 1)
                                .join("\n");
                            usageSnippets.push({
                                lineNumber: lineIndex + 1,
                                snippet: snippet,
                                actualLine: trimmedLine,
                            });
                        }
                    });

                    if (usageSnippets.length > 0) {
                        stepsUsingCurrentOutput.push({
                            name: step.name || `Step ${i + 1}`,
                            index: i,
                            usages: usageSnippets,
                        });
                    }
                }

                if (stepsUsingCurrentOutput.length > 0) {
                    context += `\n\nImportant: The following subsequent steps use the output of this step:\n`;
                    stepsUsingCurrentOutput.forEach((stepInfo) => {
                        context += `\n${stepInfo.name} (Step ${stepInfo.index}):\n`;
                        stepInfo.usages.forEach((usage, idx) => {
                            if (idx < 3) {
                                // Limit to first 3 usages per step to avoid context bloat
                                context += `  Usage at line ${usage.lineNumber}:\n${usage.snippet}\n`;
                            }
                        });
                        if (stepInfo.usages.length > 3) {
                            context += `  ... and ${
                                stepInfo.usages.length - 3
                            } more usage(s)\n`;
                        }
                    });
                    context += `\nMake sure your output is compatible with these requirements.`;
                }

                if (
                    databasesResponse?.data &&
                    databasesResponse.data.databases.length > 0 &&
                    workflowType !== "APP_V2"
                ) {
                    context += `You are using the following databases and tables: ${databasesResponse.data.databases
                        .filter((db) => db.isActiveWorkflowDatabase)
                        .map((db) =>
                            db.tables
                                .map(
                                    (table) =>
                                        `@[Database: ${db.name} Table: ${table.name}]`
                                )
                                .join(", ")
                        )
                        .join(",")}`;
                }

                if (processErrorsFinal.length > 0) {
                    context += `Warning there appears to be an error in the code. Here is the error you need to fix: ${processErrorsFinal.join(
                        "\n"
                    )}`;
                }

                // Start background task
                const payload = {
                    workflowId: workflow.id,
                    stepId: activeStep,
                    userId: workflow.userId, // You might need to get this from context
                    language,
                    context,
                    initialData,
                    stepData,
                    currentIndex: currentStepIndex,
                    message,
                    selectedApp,
                    integrations,
                    history,
                    logs: capConsoleLogsForTransmission(
                        stepExecutionLogs[activeStep] ?? []
                    ),
                    fileIds: files?.map((file) => file.id) || [],
                    selectedIntegrations,
                    mode,
                    forceModel,
                    typeOfCodeGeneration,
                    environmentName,
                    requestSuccessLogs:
                        capRequestLogsForTransmission(requestSuccessLogs),
                    tasks,
                    stepChatId,
                };
                // logGenerationPayloadDebug(
                //     "startBackgroundCodeGeneration",
                //     payload
                // );
                const response = await startCodeGeneration(payload);

                if (response?.status === 200) {
                    const taskId = response.data.publicKey;
                    setBackgroundTaskId(taskId);
                    setIsBackgroundTaskRunning(true);
                    setBackgroundTaskStatus("PENDING");

                    // Track code generation started
                    track("Code Generation Started", {
                        taskId,
                        workflowId,
                        stepId: activeStep,
                        language,
                        mode,
                        typeOfCodeGeneration,
                        forceModel,
                    });

                    // Update CodeGenerationContext
                    startCodeGenerationContext(workflowId, taskId);

                    // Start polling for task status
                    startFallbackPoll(
                        taskId,
                        activeStep,
                        workflowType,
                        stepChatId
                    );

                    return taskId;
                } else {
                    setIsGeneratingCode(false);
                    throw new Error(
                        "Failed to start background code generation"
                    );
                }
            } catch (error) {
                setIsGeneratingCode(false);
                if (error.data?.error === "INSUFFICIENT_CREDITS") {
                    toast.error(
                        t("billing.capacity.monthlyUsed") ||
                            "You've used your monthly AI capacity. Top up to keep building!",
                        {
                            duration: 8000,
                            action: {
                                label:
                                    t("billing.addCapacity") || "Add capacity",
                                onClick: () =>
                                    (window.location.href = "/billing#credits"),
                            },
                        }
                    );
                    handleGetCompanyAgentUICredit?.();
                    return null;
                } else {
                    handleGetCompanyAgentUICredit?.();
                }

                console.error(
                    "Error starting background code generation:",
                    error
                );
                throw error;
            }
        },
        [
            stepsConfig,
            steps,
            stepExecutionResult,
            stepExecutionLogs,
            databasesResponse,
            workflow,
            testData,
            startCodeGeneration,
            handleGetCompanyAgentUICredit,
        ]
    );

    // Shared handler for task status updates (used by both WebSocket and fallback poll)
    const handleTaskStatusData = useCallback(
        async (task, activeStep, stepChatId) => {
            if (!task) return;

            setBackgroundTaskStatus(task.status);

            // Update partial feedback if changed
            const lastFeedback =
                task.partialFeedback?.[task.partialFeedback.length - 1];
            const lastStoredFeedback =
                lastPartialFeedbackRef.current?.[
                    lastPartialFeedbackRef.current.length - 1
                ];
            const tasksChanged =
                JSON.stringify(lastFeedback?.result?.tasks) !==
                JSON.stringify(lastStoredFeedback?.result?.tasks);

            if (
                task.partialFeedback &&
                task.partialFeedback.length > 0 &&
                (task.partialFeedback.length !==
                    lastPartialFeedbackRef.current.length ||
                    tasksChanged)
            ) {
                const prevFeedbackLength =
                    lastPartialFeedbackRef.current.length;
                lastPartialFeedbackRef.current = task.partialFeedback;
                setPartialFeedback(task.partialFeedback);

                for (let i = task.partialFeedback.length - 1; i >= 0; i--) {
                    const feedback = task.partialFeedback[i];
                    if (feedback.result && feedback.result.tasks) {
                        setTasks(feedback.result.tasks);
                        break;
                    }
                }

                // Detect secret access/creation requests in new feedback items
                for (
                    let i = prevFeedbackLength;
                    i < task.partialFeedback.length;
                    i++
                ) {
                    const fb = task.partialFeedback[i];
                    if (fb.result?.secretAccessRequest) {
                        const name = fb.result.secretAccessRequest.secretName;
                        setRequestedSecrets((prev) =>
                            prev.includes(name) ? prev : [...prev, name]
                        );
                        setSecretModalOpen(true);
                    }
                    if (fb.result?.userActionRequest) {
                        const uar = fb.result.userActionRequest;
                        const isValid =
                            uar &&
                            typeof uar === "object" &&
                            (uar.type === "actions" ||
                                uar.type === "question") &&
                            (uar.type !== "question" ||
                                Array.isArray(uar.questions)) &&
                            (uar.type !== "actions" ||
                                Array.isArray(uar.options));
                        if (isValid) {
                            setUserActionRequest({
                                ...uar,
                                requestId:
                                    uar.requestId ||
                                    `uar_${fb.timestamp || Date.now()}_${i}`,
                            });
                        }
                    }
                    if (fb.result?.secretCreateRequest) {
                        const req = fb.result.secretCreateRequest;
                        setSecretsToCreate((prev) =>
                            prev.some((s) => s.name === req.secretName)
                                ? prev
                                : [
                                      ...prev,
                                      {
                                          name: req.secretName,
                                          reason: req.reason,
                                      },
                                  ]
                        );
                        setSecretModalOpen(true);
                    }
                }

                if (activeStep) {
                    try {
                        await fetchStepChatMessages(activeStep, stepChatId);
                    } catch (err) {
                        console.error(
                            "Error fetching messages during update:",
                            err
                        );
                    }
                }
            }

            // Bubble latest feedback type to CodeGenerationContext for the global modal
            const latestFb =
                task.partialFeedback?.[task.partialFeedback.length - 1];
            updateTaskStatus(task.status, latestFb?.type || null);

            if (task.status === "COMPLETED") {
                setIsBackgroundTaskRunning(false);

                const didModifyFiles = task?.didModifyFiles ?? false;
                setLastGenerationModifiedFiles(didModifyFiles);

                if (didModifyFiles) {
                    setRequestSuccessLogs([]);
                    setStepExecutionLogs((prev) => ({
                        ...prev,
                        [activeStep]: [],
                    }));
                }

                didSetup.current = false;
                await refetchWorkflow();
                setTimeout(() => {
                    setCodeVersionNumber((prev) => prev + 1);
                }, 2400);

                setTimeout(() => {
                    setIsGeneratingCode(false);
                    completeCodeGeneration();
                    setPartialFeedback([]);
                    lastPartialFeedbackRef.current = [];
                }, 100);

                if (task?.diff) {
                    try {
                        await addStepChatMessage({
                            stepId: activeStep,
                            role: "diff",
                            content: task.diff,
                            stepChatId,
                        });
                    } catch (error) {
                        console.error(
                            "Error adding diff message to chat:",
                            error
                        );
                    }
                }

                await fetchStepChatMessages(activeStep, stepChatId, true);
                handleGetCompanyAgentUICredit?.();
                toast.success("Code generation completed successfully!");
            } else if (task.status === "FAILED") {
                setIsBackgroundTaskRunning(false);
                setIsGeneratingCode(false);
                setBackgroundTaskId(null);
                setGenerationError(
                    task.error || task.output?.error || "Code generation failed"
                );
                completeCodeGeneration();
                setPartialFeedback([]);
                lastPartialFeedbackRef.current = [];

                // Clean up fallback poll
                if (fallbackPollRef.current) {
                    clearInterval(fallbackPollRef.current);
                    fallbackPollRef.current = null;
                }
                activeTaskParamsRef.current = null;

                // Show error in chat so user sees what went wrong
                const errorMsg =
                    task.error ||
                    task.output?.error ||
                    "Code generation failed";
                setChatMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: errorMsg,
                        createdAt: new Date(),
                        isError: true,
                    },
                ]);

                toast.error("Code generation failed");
            } else if (task.status === "CANCELLED") {
                setIsBackgroundTaskRunning(false);
                setBackgroundTaskStatus("CANCELLED");
                completeCodeGeneration();
                setPartialFeedback([]);
                lastPartialFeedbackRef.current = [];
                toast.info("Code generation was cancelled");
            }
        },
        [
            fetchStepChatMessages,
            handleGetCompanyAgentUICredit,
            addStepChatMessage,
            refetchWorkflow,
            completeCodeGeneration,
            updateTaskStatus,
        ]
    );

    // Store active task params for WebSocket and fallback poll
    const activeTaskParamsRef = useRef(null);
    const fallbackPollRef = useRef(null);

    // WebSocket hook for real-time task updates
    const { isConnected: wsConnected, connectionFailed: wsConnectionFailed } =
        useTaskWebSocket({
            taskId: backgroundTaskId,
            enabled: isBackgroundTaskRunning,
            onUpdate: useCallback(
                (data) => {
                    const params = activeTaskParamsRef.current;
                    if (!params) return;
                    const task = data?.data || data;
                    handleTaskStatusData(
                        task,
                        params.activeStep,
                        params.stepChatId
                    );
                },
                [handleTaskStatusData]
            ),
        });

    // Fallback poll: 10s when WebSocket is connected, 2s when it's not
    const startFallbackPoll = useCallback(
        (taskId, activeStep, workflowType, stepChatId) => {
            if (!taskId) return;

            setIsGeneratingCode(true);
            pollRetryCountRef.current = 0;

            activeTaskParamsRef.current = {
                taskId,
                activeStep,
                workflowType,
                stepChatId,
            };

            // Clear any existing fallback poll
            if (fallbackPollRef.current) {
                clearInterval(fallbackPollRef.current);
            }

            const poll = async () => {
                try {
                    const response = await getTaskStatus({ taskId });
                    if (response?.status === 200) {
                        pollRetryCountRef.current = 0;
                        await handleTaskStatusData(
                            response.data,
                            activeStep,
                            stepChatId
                        );

                        // Stop polling on terminal states
                        const status = response.data?.status;
                        if (
                            status === "COMPLETED" ||
                            status === "FAILED" ||
                            status === "CANCELLED"
                        ) {
                            clearInterval(fallbackPollRef.current);
                            fallbackPollRef.current = null;
                            activeTaskParamsRef.current = null;
                        }
                    }
                } catch (error) {
                    console.error("Error polling task status:", error);
                    pollRetryCountRef.current += 1;

                    if (pollRetryCountRef.current >= 10) {
                        clearInterval(fallbackPollRef.current);
                        pollRetryCountRef.current = 0;
                        setBackgroundTaskStatus("RECONNECTING");

                        // Retry after 30s
                        setTimeout(() => {
                            if (activeTaskParamsRef.current) {
                                startFallbackPoll(
                                    taskId,
                                    activeStep,
                                    workflowType,
                                    stepChatId
                                );
                            }
                        }, 30000);
                    }
                }
            };

            // Initial poll immediately
            poll();

            // Continue polling at interval
            fallbackPollRef.current = setInterval(
                poll,
                wsConnected ? 10000 : 2000
            );

            return () => {
                clearInterval(fallbackPollRef.current);
                fallbackPollRef.current = null;
            };
        },
        [getTaskStatus, handleTaskStatusData, wsConnected]
    );

    // Adjust poll interval when WebSocket connection status changes
    React.useEffect(() => {
        const params = activeTaskParamsRef.current;
        if (!params || !fallbackPollRef.current) return;

        // Restart poll with adjusted interval
        clearInterval(fallbackPollRef.current);
        const poll = async () => {
            try {
                const response = await getTaskStatus({ taskId: params.taskId });
                if (response?.status === 200) {
                    await handleTaskStatusData(
                        response.data,
                        params.activeStep,
                        params.stepChatId
                    );
                    const status = response.data?.status;
                    if (
                        status === "COMPLETED" ||
                        status === "FAILED" ||
                        status === "CANCELLED"
                    ) {
                        clearInterval(fallbackPollRef.current);
                        fallbackPollRef.current = null;
                        activeTaskParamsRef.current = null;
                    }
                }
            } catch (error) {
                console.error("Error polling task status:", error);
            }
        };
        fallbackPollRef.current = setInterval(poll, wsConnected ? 10000 : 2000);
    }, [wsConnected, getTaskStatus, handleTaskStatusData]);

    const cancelBackgroundTask = useCallback(async () => {
        if (!backgroundTaskId) return;

        try {
            const response = await cancelCodeGeneration({
                taskId: backgroundTaskId,
            });

            if (response?.status === 200) {
                setBackgroundTaskStatus("CANCELLED");
                setIsBackgroundTaskRunning(false);
                toast.info("Code generation cancelled");
                setIsGeneratingCode(false);
                setBackgroundTaskId(null);
                setPartialFeedback([]);
                lastPartialFeedbackRef.current = [];

                // Clean up fallback poll
                if (fallbackPollRef.current) {
                    clearInterval(fallbackPollRef.current);
                    fallbackPollRef.current = null;
                }
                activeTaskParamsRef.current = null;

                // Reset CodeGenerationContext so re-mounting doesn't show stale "generating" state
                completeCodeGeneration();
            }
        } catch (error) {
            console.error("Error cancelling background task:", error);
            toast.error("Failed to cancel code generation");
        }
    }, [backgroundTaskId, cancelCodeGeneration, completeCodeGeneration]);

    const handleChat = useCallback(
        async (
            message,
            messages,
            stagedFiles = [],
            processErrorsFinal = [],
            mode = "code_generation",
            forceModel = "auto" || "default",
            environmentName = null,
            tasks = []
        ) => {
            let localActiveStep = activeStep;
            setIsChatLoading(true);
            setPartialFeedback([]);
            setUserActionRequest(null);

            // Only reset tasks if all tasks are completed or cancelled
            setTasks((currentTasks) => {
                const allCompleted = currentTasks.every(
                    (task) =>
                        task.status === "completed" ||
                        task.status === "done" ||
                        task.status === "cancelled"
                );
                return allCompleted ? [] : currentTasks;
            });

            lastPartialFeedbackRef.current = [];

            let currentWorkflowType = workflow.workflowType;

            let localMessages = [...messages];

            const currentStepIndex = steps.findIndex(
                (step) => step.id === activeStep
            );

            handleResetFailedStep();

            // Handle human support mode - use the centralized hook
            // Hook handles messages, toasts, and errors internally
            if (mode === "human_support") {
                await humanSupport.sendMessage(message, stagedFiles);
                setIsChatLoading(false);
                return;
            }

            if (!localActiveStep || localActiveStep === "") {
                const step = await addStep("CODE_EDITOR", {
                    language: "javascript",
                    code: "",
                });

                localActiveStep = step.id;
                setActiveStep(localActiveStep);
            }

            if (
                bypassWarning.current === false &&
                currentStepIndex > 0 &&
                (stepExecutionResult == null ||
                    stepExecutionResult.length < currentStepIndex)
            ) {
                setChatMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: t("common.stepNotRunWarning"),
                        buttonText: t("common.continueGenerating"),
                        action: "bypassWarning",
                        createdAt: new Date(),
                    },
                ]);

                currentMessage.current = message;
                setIsChatLoading(false);

                return;
            }

            bypassWarning.current = false;
            currentMessage.current = "";

            setChatMessages((prev) => [
                ...prev,
                {
                    role: "human",
                    content: message,
                    createdAt: new Date(),
                    files: stagedFiles,
                },
            ]);

            let context = "";

            // Add current page viewing context
            if (currentPageContext.selectedPageFilePath) {
                // We have an exact file path
                const routeInfo = currentPageContext.currentRoute
                    ? ` (route: ${currentPageContext.currentRoute})`
                    : "";
                context += `\nThe user is currently viewing: "${currentPageContext.selectedPageFilePath}"${routeInfo}. If the user asks to make changes without specifying a file, assume they mean this file.`;
            } else if (currentPageContext.currentRoute) {
                const extractPageName = (route) => {
                    const segments = route
                        .replace(/^\//, "")
                        .split("/")
                        .filter(Boolean);
                    return segments.length > 0
                        ? segments[segments.length - 1]
                        : route;
                };
                const pageName =
                    currentPageContext.selectedPageName ||
                    extractPageName(currentPageContext.currentRoute);
                context += `\nThe user is currently viewing route: "${currentPageContext.currentRoute}" (page: ${pageName}). If the user asks to make changes, try to find the corresponding page file.`;
            } else if (currentPageContext.selectedRightPanel) {
                context += `\nThe user is currently viewing the "${currentPageContext.selectedRightPanel}" panel in the dashboard.`;
            }

            // Add canvas step selection context
            if (selectedCanvasStepId) {
                const selectedStep = steps.find(
                    (s) => s.id === selectedCanvasStepId
                );
                if (selectedStep) {
                    const stepIndex = steps.indexOf(selectedStep);
                    const stepName =
                        selectedStep.name || `Step ${stepIndex + 1}`;
                    const stepLang =
                        selectedStep.config?.language || "javascript";
                    context += `\nThe user is currently viewing/inspecting step: "${stepName}" (id: ${selectedCanvasStepId}, position: ${
                        stepIndex + 1
                    }, language: ${stepLang}). If the user asks to make changes without specifying a step, assume they mean this step.`;
                }
            }

            if (processErrorsFinal.length > 0) {
                context += `Warning there appears to be an error in the code. Here is the error you need to fix: ${processErrorsFinal.join(
                    "\n"
                )}`;
            }

            const stepData = getCurrentStepData({
                activeStep,
                stepExecutionResult,
                steps,
            });

            let response = null;

            // Retry function with exponential backoff
            const retryWithBackoff = async (fn, maxRetries = 3) => {
                let lastError = null;
                for (let attempt = 0; attempt < maxRetries; attempt++) {
                    try {
                        return await fn();
                    } catch (error) {
                        lastError = error;

                        // Don't retry if it's a 402 error
                        if (
                            error?.status === 402 ||
                            error?.response?.status === 402
                        ) {
                            throw error;
                        }

                        if (attempt < maxRetries - 1) {
                            const delay = Math.pow(2, attempt) * 2000;
                            await new Promise((resolve) =>
                                setTimeout(resolve, delay)
                            );
                        }
                    }
                }
                throw lastError;
            };

            // Use workflowId for chat persistence (decoupled from steps)
            const effectiveChatStepId = chatStepId || localActiveStep;

            let newStepChatId = selectedStepChatId;

            try {
                const chatMessageResult = await addStepChatMessage({
                    stepId: effectiveChatStepId,
                    role: "human",
                    mode: mode,
                    content: message,
                    fileIds: stagedFiles?.map((file) => file.id) || [],
                    stepChatId: selectedStepChatId,
                });
                if (chatMessageResult?.stepChatId) {
                    newStepChatId = chatMessageResult.stepChatId;
                }
            } catch (error) {
                setIsChatLoading(false);
                throw new Error(
                    t("common.connectionError") ||
                        "Unable to connect to the server. Please check your connection and try again."
                );
            }

            const isBackgroundGeneration =
                workflow.workflowType === "APP_V2" ||
                workflow.workflowType === "AUTOMATION";

            const apiCallParams = {
                workflowId,
                userRequest: message ?? "",
                stepId: localActiveStep,
                history: isBackgroundGeneration
                    ? []
                    : getHistoryForChat(localMessages),
                logs: capConsoleLogsForTransmission(
                    stepExecutionLogs[activeStep] ?? []
                ),
                context,
                stepData,
                initialData: testData,
                fileIds: stagedFiles?.map((file) => file.id) || [],
                currentIndex:
                    currentStepIndex === -1
                        ? stepData.length > 0
                            ? stepData.length - 1
                            : 0
                        : currentStepIndex,
                mode,
                forceModel,
                environmentName,
                requestSuccessLogs:
                    capRequestLogsForTransmission(requestSuccessLogs),
                tasks,
                stepChatId: newStepChatId,
            };
            // logGenerationPayloadDebug("handleChat", apiCallParams);

            if (isBackgroundGeneration) {
                // For APP_V2, start background task immediately and poll for updates
                // This provides real-time feedback without streaming (firewall friendly)
                setIsChatLoading(false);

                // Track workflow usage when chat is sent
                trackWorkflowUsage(workflowId);

                // Start background code generation with polling
                try {
                    await startBackgroundCodeGeneration({
                        requestSuccessLogs,
                        activeStep: effectiveChatStepId,
                        message: message,
                        environmentName: environmentName,
                        selectedApp:
                            stepsConfig[localActiveStep]?.language ??
                            "javascript",
                        integrations:
                            stepsConfig[localActiveStep]?.integrations ?? [],
                        currentCode: stepsConfig[localActiveStep]?.code ?? "",
                        history: getHistoryForChat(localMessages, tasks),
                        selectedIntegrations:
                            getSelectedIntegrations(localMessages),
                        language:
                            stepsConfig[localActiveStep]?.language ??
                            "javascript",
                        processErrorsFinal,
                        initialData: testData,
                        files: collectFilesFromHistory(localMessages),
                        mode,
                        typeOfCodeGeneration: "new_code",
                        forceModel,
                        tasks,
                        context,
                        stepChatId: newStepChatId,
                        workflowType: currentWorkflowType,
                    });
                } catch (error) {
                    if (
                        error?.status === 402 ||
                        error?.response?.status === 402 ||
                        error?.data?.error === "INSUFFICIENT_CREDITS"
                    ) {
                        toast.error(
                            t("billing.capacity.monthlyUsed") ||
                                "You've used your monthly AI capacity.",
                            {
                                description:
                                    t("billing.capacity.topUpDescription") ||
                                    "Top up to keep building!",
                                duration: 8000,
                                action: {
                                    label:
                                        t("billing.addCapacity") ||
                                        "Add capacity",
                                    onClick: () => {
                                        localStorage.setItem(
                                            "billing_return_url",
                                            window.location.pathname +
                                                window.location.search
                                        );
                                        navigate("/billing#credits");
                                    },
                                },
                            }
                        );
                    }
                }

                // Exit early - polling will handle the rest
                return;
            } else {
                try {
                    response = await retryWithBackoff(() =>
                        chatWithWorkflowCall(apiCallParams)
                    );
                } catch (error) {
                    if (
                        error?.status === 402 ||
                        error?.response?.status === 402
                    ) {
                        setIsChatLoading(false);
                        toast.error(
                            t("billing.capacity.monthlyUsed") ||
                                "You've used your monthly AI capacity.",
                            {
                                description:
                                    t("billing.capacity.topUpDescription") ||
                                    "Top up to keep building!",
                                duration: 8000,
                                action: {
                                    label:
                                        t("billing.addCapacity") ||
                                        "Add capacity",
                                    onClick: () => {
                                        localStorage.setItem(
                                            "billing_return_url",
                                            window.location.pathname +
                                                window.location.search
                                        );
                                        navigate("/billing#credits");
                                    },
                                },
                            }
                        );
                        return;
                    }
                    setIsChatLoading(false);
                    throw error;
                }
            }

            // Track workflow usage when chat is sent
            trackWorkflowUsage(workflowId);

            // Update selectedStepChatId with the returned stepChatId for subsequent calls
            if (response.data?.workflowPlan?.stepChatId) {
                setSelectedStepChatId(response.data.workflowPlan.stepChatId);
            }

            if (response.data?.workflowType) {
                currentWorkflowType = response.data.workflowType;
            }

            localMessages.push({
                role: "human",
                content: message,
                files: stagedFiles,
            });

            await addStepChatMessage({
                stepId: localActiveStep,
                role: "assistant",
                mode: mode,
                content: response.data?.workflowPlan?.message,
                functionCall: {},
                functionResponse: {},
                language: response.data?.workflowPlan?.selectedApp ?? "none",
                integrationTags:
                    response.data?.workflowPlan?.integrations ?? [],
                stepChatId: newStepChatId,
            });

            setIsChatLoading(false);

            if (response.data?.workflowPlan?.requestSecretAccess) {
                const hasAccess = await checkSecretAccess(
                    response.data.workflowPlan.requestSecretAccess
                );
                if (!hasAccess) {
                    localMessages.push({
                        role: "assistant",
                        content:
                            "I cannot proceed without access to the requested secrets.",
                    });
                    await addStepChatMessage({
                        stepId: localActiveStep,
                        role: "assistant",
                        mode: mode,
                        content:
                            "I cannot proceed without access to the requested secrets.",
                        stepChatId: newStepChatId,
                    });
                    return;
                }
            }

            localMessages.push({
                role: "assistant",
                content: response.data?.workflowPlan?.message,
                language: response.data?.workflowPlan?.selectedApp ?? "none",
                integrationTags:
                    response.data?.workflowPlan?.integrations ?? [],
            });

            if (response.data?.workflowPlan?.tasks) {
                setTasks(response.data.workflowPlan.tasks);
            }

            if (
                response.data?.workflowPlan?.nextStep === "startGeneratingCode"
            ) {
                // Start background code generation task
                startBackgroundCodeGeneration({
                    requestSuccessLogs,
                    activeStep: localActiveStep,
                    message: message,
                    environmentName: environmentName,
                    selectedApp:
                        response.data?.workflowPlan?.selectedApp === "none"
                            ? stepsConfig[localActiveStep]?.language ??
                              "javascript"
                            : response.data?.workflowPlan?.selectedApp,
                    integrations:
                        !response.data?.workflowPlan?.integrations ||
                        response.data?.workflowPlan?.integrations.length === 0
                            ? stepsConfig[localActiveStep]?.integrations ?? []
                            : response.data?.workflowPlan?.integrations,
                    currentCode: stepsConfig[localActiveStep]?.code ?? "",
                    history: getHistoryForChat(
                        localMessages,
                        response.data?.workflowPlan?.tasks || tasks
                    ),
                    selectedIntegrations:
                        getSelectedIntegrations(localMessages),
                    language:
                        response.data?.workflowPlan?.selectedApp ??
                        stepsConfig[localActiveStep]?.language ??
                        "javascript",
                    processErrorsFinal,
                    initialData: testData,
                    files: collectFilesFromHistory(localMessages),
                    mode,
                    typeOfCodeGeneration:
                        response.data?.workflowPlan?.typeOfCodeGeneration ??
                        "new_code",
                    forceModel,
                    tasks: response.data?.workflowPlan?.tasks || tasks,
                    stepChatId: newStepChatId,
                    workflowType: currentWorkflowType,
                });
            }
        },
        [
            activeStep,
            chatStepId,
            startBackgroundCodeGeneration,
            stepsConfig,
            addStep,
            workflow,
            databasesResponse,
            processErrors,
            t,
            testData,
            steps,
            stepExecutionResult,
            activeStepData,
            addStepChatMessage,
            checkSecretAccess,
            currentPageContext,
            selectedCanvasStepId,
            requestSuccessLogs,
            chatWithWorkflowCall,
            getCurrentStepData,
            handleResetFailedStep,
            navigate,
            stepExecutionLogs,
            userLanguage,
            workflowId,
            selectedStepChatId,
            humanSupport,
            refetchWorkflow,
        ]
    );

    const handleErrorLog = useCallback(
        (error) => {
            setProcessErrors((prev) => [...prev, error]);
            // disable this for now
            // setChatMessages((prev) => [
            //     ...prev,
            //     {
            //         role: "assistant",
            //         content: t("common.errorFixOffer"),
            //         buttonText: t("common.fixIt"),
            //         action: "fixCodeError",
            //     },
            // ]);
        },
        [t]
    );

    const handleSendMessage = useCallback((message) => {
        setChatMessages((prev) => [
            ...prev,
            {
                role: "assistant",
                content: message,
            },
        ]);
    }, []);

    const handleAction = useCallback(
        (action, selectedEnvironment = null) => {
            if (action === "fixCodeError") {
                handleChat(
                    "Fix the code",
                    chatMessages,
                    [],
                    processErrors,
                    selectedEnvironment
                );
            } else if (action === "bypassWarning") {
                bypassWarning.current = true;
                handleChat(
                    currentMessage.current,
                    chatMessages,
                    [],
                    processErrors,
                    selectedEnvironment
                );
            } else if (action === "runCode") {
                // Clear helper messages and show running message
                setChatHelperMessages([
                    {
                        role: "assistant",
                        content: "Running your code...",
                        createdAt: new Date(),
                    },
                ]);

                // Execute the current step
                const stepData = getCurrentStepData({
                    activeStep,
                    stepExecutionResult,
                    steps,
                });
                startWorkflow(stepData, activeStep);
            } else if (action.startsWith("applySuggestion:")) {
                if (isGeneratingCode) return;
                const suggestion = action.slice("applySuggestion:".length);
                setChatHelperMessages((prev) =>
                    prev.filter((msg) => msg.action !== action)
                );
                handleChat(
                    suggestion,
                    chatMessages,
                    [],
                    processErrors,
                    selectedEnvironment
                );
            } else if (action.startsWith("userAction:")) {
                if (isGeneratingCode) return;
                const payload = JSON.parse(action.slice("userAction:".length));
                const { value: selectedValue, action: optAction } = payload;
                setUserActionRequest(null);

                // Determine intent: use action field, fall back to value
                const isExitPlan =
                    optAction === "exit_plan" ||
                    (!optAction && selectedValue === "apply_plan");
                const isContinue =
                    optAction === "continue" ||
                    (!optAction && selectedValue === "modify_plan");

                if (isExitPlan) {
                    setChatMode("code_generation");
                    handleChat(
                        `The user approved the plan. Implement it now.`,
                        chatMessages,
                        [],
                        processErrors,
                        "code_generation",
                        "auto",
                        selectedEnvironment
                    );
                } else if (isContinue) {
                    // Stay in plan mode — user can type in the chat input
                    // No message sent, just clear the action UI
                } else {
                    // Generic selection — send value back in plan mode
                    handleChat(
                        selectedValue,
                        chatMessages,
                        [],
                        processErrors,
                        "plan",
                        "auto",
                        selectedEnvironment
                    );
                }
            }
        },
        [
            handleChat,
            chatMessages,
            processErrors,
            startWorkflow,
            activeStep,
            steps,
            stepExecutionResult,
            getCurrentStepData,
            isGeneratingCode,
            setChatMode,
        ]
    );

    const activeStepIndex = steps.findIndex((step) => step.id === activeStep);

    const initializeStepConfig = useCallback(
        (stepId, step) => {
            if (!stepsConfig[stepId]) {
                setStepsConfig((prev) => ({
                    ...prev,
                    [stepId]: {
                        code: step?.config?.code || "",
                        language: step?.config?.language || "javascript",
                    },
                }));
            }
        },
        [stepsConfig]
    );

    const handleSetActiveStep = useCallback(async (stepId) => {
        setActiveStep(stepId);
        // Chat is holistic — don't reload messages when switching steps
        // Messages always live on the first step's StepChat
    }, []);

    // Update current page context - called from AppPreviewDashboard or ChatContext
    const updateCurrentPageContext = useCallback((pageContext) => {
        setCurrentPageContext((prev) => ({
            ...prev,
            ...pageContext,
        }));
    }, []);

    const currentStepCodeVersion = React.useMemo(() => {
        return steps.find((step) => step.id === activeStep)?.currentVersion;
    }, [steps, activeStep]);

    const rollbackWorkflowStep = React.useCallback(
        async (stepId, versionId, environmentName) => {
            try {
                const response = await rollbackStep({
                    stepId,
                    versionId,
                    environmentName,
                });

                if (response?.data) {
                    // Update the local step with the rolled back data
                    setSteps((prevSteps) =>
                        prevSteps.map((step) =>
                            step.id === stepId
                                ? { ...step, ...response.data.workflowStep }
                                : step
                        )
                    );

                    if (response.data.workflowStep?.config?.code) {
                        setStepsConfig((prevConfig) => ({
                            ...prevConfig,
                            [stepId]: {
                                code: response.data.workflowStep.config.code,
                                language:
                                    response.data.workflowStep.config.language,
                            },
                        }));
                    }

                    toast.success(
                        `Successfully rolled back to version ${response.data.rolledBackToVersion}`
                    );

                    return response.data;
                }
            } catch (error) {
                toast.error("Failed to rollback step");
            }
        },
        [rollbackStep]
    );

    const resyncStepsConfig = useCallback(() => {
        didSetup.current = false;
    }, []);

    React.useEffect(() => {
        if (steps.length > 0 && !didSetup.current) {
            didSetup.current = true;
            const newConfig = {};
            steps.forEach((step) => {
                newConfig[step.id] = {
                    ...step?.config,
                };
            });
            setStepsConfig(newConfig);
        } else if (steps.length === 0) {
            setStepsConfig({});
            didSetup.current = false;
        }
    }, [steps]);

    React.useEffect(() => {
        if (
            stepExecutionLogs[activeStep] &&
            stepExecutionLogs[activeStep].length > 0 &&
            stepExecutionLogs[activeStep].some(
                (log) =>
                    log.args &&
                    Array.isArray(log.args) &&
                    log.args.some((arg) =>
                        typeof arg === "string"
                            ? arg?.toLowerCase().includes("not enough credits")
                            : false
                    )
            ) &&
            stepExecutionLogs[activeStep].some(
                (log) =>
                    log.type === "alert" &&
                    log.args &&
                    Array.isArray(log.args) &&
                    log.args.some((arg) =>
                        typeof arg === "string"
                            ? arg?.toLowerCase().includes("not enough credits")
                            : false
                    )
            )
        ) {
            setChatMessages((prev) => [
                ...prev,
                {
                    role: "alert",
                    content: t("common.insufficientCreditsAlert"),
                },
            ]);
        }
    }, [stepExecutionLogs, activeStep, t]);

    // Check for running tasks when workflow loads (runs only once per workflowId)
    React.useEffect(() => {
        async function checkRunningTasks() {
            if (
                !workflowId ||
                workflowId === "null" ||
                workflowId === "undefined" ||
                didCheckRunningTasksRef.current === workflowId
            ) {
                return;
            }

            didCheckRunningTasksRef.current = workflowId;

            try {
                const response = await getRunningTasksForWorkflow({
                    workflowId,
                });
                const task = response?.data?.task;

                if (
                    task &&
                    (task.status === "PENDING" || task.status === "RUNNING")
                ) {
                    setBackgroundTaskId(task.publicKey);
                    setIsBackgroundTaskRunning(true);
                    setBackgroundTaskStatus(task.status);
                    setIsGeneratingCode(true);
                    startCodeGenerationContext(workflowId, task.publicKey);

                    if (task.partialFeedback?.length > 0) {
                        setPartialFeedback(task.partialFeedback);
                        lastPartialFeedbackRef.current = task.partialFeedback;
                    }

                    if (task.stepId) {
                        setActiveStep(task.stepId);
                    }

                    startFallbackPoll(
                        task.publicKey,
                        task.stepId || activeStep,
                        workflow?.workflowType,
                        selectedStepChatId
                    );
                }
            } catch (error) {
                console.error("Error checking for running tasks:", error);
            }
        }

        checkRunningTasks();

        // Human support ticket loading is handled by useHumanSupport hook automatically

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowId]);

    // Resume fallback poll when network comes back online
    React.useEffect(() => {
        const handleOnline = () => {
            const params = activeTaskParamsRef.current;
            if (!params || fallbackPollRef.current) return;

            // Restart the fallback poll if it was stopped due to errors
            startFallbackPoll(
                params.taskId,
                params.activeStep,
                params.workflowType,
                params.stepChatId
            );
        };

        window.addEventListener("online", handleOnline);
        return () => window.removeEventListener("online", handleOnline);
    }, [startFallbackPoll]);

    React.useEffect(() => {
        if (
            workflowId &&
            activeStep &&
            workflowId != null &&
            workflowId !== "null" &&
            workflowId !== "undefined"
        ) {
            // Chat is holistic — use workflowId for message persistence
            fetchStepChatMessages(null, null);
            fetchChatHistory();
        } else if (!workflowId) {
            setActiveStep(null);
            setChatMessages([]);
            setStepChatHistory([]);
            setChatHistoryCursor(null);
            setHasMoreChatHistory(false);
            setSelectedStepChatId(null);
            setTasks([]);
            setChatHelperMessages([]);
            setHasMoreChatMessages(false);
            setChatMessagesCursor(null);
            didCheckRunningTasksRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowId, activeStep]);

    return {
        activeStep,
        setActiveStep: handleSetActiveStep,
        chatMessages,
        chatHelperMessages,
        clearHelperMessages,
        onClearHelperMessages,
        tasks,
        setTasks,

        handleSendMessage,
        isChatLoading:
            isChatLoading ||
            getActiveStepChatLoading ||
            archiveStepChatLoading ||
            insertStepChatMessageLoading,
        chatError,
        handleChat,
        getStepConfig,
        isGeneratingCode,
        sendHelperMessage,
        appendHelperMessage,
        handleAddStep,
        initializeStepConfig,
        handleSetStepsConfig,
        resyncStepsConfig,
        removeStep,
        handleAction,
        handleErrorLog,
        stepsConfig,
        activeStepIndex,
        archiveStepChatForStep,
        activeStepData,
        rollbackWorkflowStep,
        isLoadingRollback,
        errorRollback,
        missingSecretWarnings,
        setMissingSecretWarnings,
        codeVersionNumber,

        // Background task functionality
        startBackgroundCodeGeneration,
        cancelBackgroundTask,
        backgroundTaskId,
        backgroundTaskStatus,
        isBackgroundTaskRunning,
        partialFeedback,
        lastGenerationModifiedFiles,
        isStartingCodeGeneration,
        isGettingTaskStatus,
        isCancellingCodeGeneration,
        startCodeGenerationError,
        getTaskStatusError,
        cancelCodeGenerationError,
        currentStepCodeVersion,
        isCompactingChatMessages,
        // Secret Modal Exports
        secretModalOpen,
        requestedSecrets,
        secretsToCreate,
        handleGrantSecretAccess,
        handleCreateSecrets,
        handleDenySecretAccess,
        isGrantingAccess,

        // Chat History Exports
        stepChatHistory,
        fetchChatHistory,
        loadMoreChatHistory,
        hasMoreChatHistory,
        isLoadingMoreChatHistory,
        selectedStepChatId,
        selectChatSession,
        startNewChat,

        // Chat Rating
        chatRating,
        handleRateChat,

        // Current Page Context - for AI awareness of what user is viewing
        currentPageContext,
        updateCurrentPageContext,
        setRequestSuccessLogs,

        // Canvas step selection - for AI awareness of which step user is inspecting
        selectedCanvasStepId,
        setSelectedCanvasStepId,

        // Human Support - from centralized hook
        humanSupport,
        humanSupportTicketId: humanSupport.ticket?.id || null,
        humanSupportTicket: humanSupport.ticket,
        humanSupportMessages: humanSupport.messages,
        isLoadingHumanSupportTicket: humanSupport.isLoading,
        isSendingHumanSupportMessage: humanSupport.isSending,
        hasHumanSupportTicket: humanSupport.hasTicket,

        // Chat mode - for switching between code/plan/human support
        chatMode,
        setChatMode,

        // User action request — interactive UI from AI
        userActionRequest,
        setUserActionRequest,

        // Refetch chat messages
        fetchStepChatMessages,
        fetchOlderChatMessages,
        hasMoreChatMessages,
    };
}

export function ProvideWorkflowGeneration({ children, workflowId }) {
    const generation = useProvideWorkflowGeneration({ workflowId });
    return (
        <WorkflowGenerationContext.Provider value={generation}>
            {children}
            <SecretAccessModal
                isOpen={generation.secretModalOpen}
                onClose={generation.handleDenySecretAccess}
                secrets={generation.requestedSecrets}
                secretsToCreate={generation.secretsToCreate}
                onGrant={generation.handleGrantSecretAccess}
                onCreate={generation.handleCreateSecrets}
                isGranting={generation.isGrantingAccess}
            />
        </WorkflowGenerationContext.Provider>
    );
}

export function useWorkflowGeneration() {
    const context = useContext(WorkflowGenerationContext);
    if (!context) {
        throw new Error(
            "useWorkflowGeneration must be used within a ProvideWorkflowGeneration"
        );
    }
    return context;
}
